import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useSearch } from '@tanstack/react-router';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { Film, Star, Trash2 } from 'lucide-react';
import { useAuth } from '@/features/auth';
import { api } from '@watchparty/shared';
import { tmdbImage, workTypeLabel } from '@/lib/utils';
import { ActivityFeedTab } from './ActivityFeedTab';
import { MembersTab } from './MembersTab';
import { DiscussionTab } from './DiscussionTab';
import { DiscoverTab } from './DiscoverTab';
import { Dialog, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/Dialog';
// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const STATUS_WATCHED = 1;
const STATUS_WATCHING = 2;
const STATUS_WANTS_TO_WATCH = 3;
const filterLabels = {
    all: 'All',
    wantToWatch: 'Want to Watch',
    watching: 'Watching',
    watched: 'Watched',
};
function statusMatchesFilter(myStatus, filter) {
    if (filter === 'all')
        return true;
    if (filter === 'watched')
        return myStatus === STATUS_WATCHED;
    if (filter === 'watching')
        return myStatus === STATUS_WATCHING;
    if (filter === 'wantToWatch')
        return myStatus === STATUS_WANTS_TO_WATCH;
    return true;
}
function TabNav({ activeTab, onChange, isOwner }) {
    const tabs = [
        {
            id: 'watchlist',
            label: 'WatchList',
            renderLabel: (isActive) => (_jsxs("span", { style: { opacity: isActive ? 1 : 0.4 }, children: [_jsx("span", { style: { color: 'var(--color-text)' }, children: "Watch" }), _jsx("span", { style: { color: 'var(--color-accent)' }, children: "List" })] })),
        },
        { id: 'discover', label: 'Discover' },
        { id: 'activity', label: 'Activity' },
        { id: 'members', label: 'Members' },
        { id: 'discussion', label: 'Discussion' },
        ...(isOwner ? [{ id: 'settings', label: 'Settings' }] : []),
    ];
    return (_jsx("div", { style: { display: 'flex', borderBottom: '1px solid var(--color-border)', marginBottom: '24px', overflowX: 'auto' }, children: tabs.map((tab) => (_jsx("button", { onClick: () => onChange(tab.id), style: {
                padding: '10px 16px',
                fontFamily: 'var(--font-family-body)',
                fontSize: '0.875rem',
                fontWeight: 700,
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                borderBottom: activeTab === tab.id ? '2px solid var(--color-accent)' : '2px solid transparent',
                color: activeTab === tab.id ? 'var(--color-accent)' : 'var(--color-text-muted)',
                marginBottom: '-1px',
                transition: 'color 200ms, background-color 200ms, opacity 200ms',
                whiteSpace: 'nowrap',
            }, children: tab.renderLabel ? tab.renderLabel(activeTab === tab.id) : tab.label }, tab.id))) }));
}
// ---------------------------------------------------------------------------
// PartyHeader
// ---------------------------------------------------------------------------
function PartyHeader({ party }) {
    return (_jsx("div", { style: { marginBottom: '24px' }, children: _jsx("h1", { style: {
                fontFamily: 'var(--font-family-heading)',
                fontSize: 'clamp(1.25rem, 3vw, 1.75rem)',
                fontWeight: 700,
                color: 'var(--color-text)',
                margin: 0,
            }, children: party.name }) }));
}
// ---------------------------------------------------------------------------
// WatchStatusControl (overlay)
// ---------------------------------------------------------------------------
const statusOptions = [
    { value: STATUS_WANTS_TO_WATCH, label: 'Want to Watch' },
    { value: STATUS_WATCHING, label: 'Watching' },
    { value: STATUS_WATCHED, label: 'Watched' },
];
function WatchStatusControl({ workId, currentStatus, partyId, }) {
    const queryClient = useQueryClient();
    const handleClick = useCallback(async (status) => {
        const key = ['party', partyId, 'watchlist'];
        const clearing = status === currentStatus;
        // Optimistic update
        queryClient.setQueryData(key, (old) => {
            if (!old)
                return old;
            return {
                ...old,
                pages: old.pages.map((page) => ({
                    ...page,
                    items: page.items.map((item) => item.workId === workId
                        ? { ...item, myStatus: clearing ? null : status, ...(clearing ? { myRating: null } : {}) }
                        : item),
                })),
            };
        });
        try {
            if (clearing) {
                await api.delete(`/users/me/works/${workId}/status`);
            }
            else {
                await api.put(`/users/me/works/${workId}/status`, { status });
            }
        }
        catch {
            queryClient.invalidateQueries({ queryKey: key });
        }
    }, [workId, currentStatus, partyId, queryClient]);
    return (_jsx("div", { style: { display: 'flex', gap: '6px', flexWrap: 'wrap', justifyContent: 'center' }, children: statusOptions.map((opt) => {
            const active = currentStatus === opt.value;
            return (_jsx("button", { onClick: (e) => { e.stopPropagation(); handleClick(opt.value); }, style: {
                    padding: '4px 8px',
                    borderRadius: 'var(--radius-full)',
                    border: `1px solid ${active ? 'transparent' : 'rgba(255,255,255,0.4)'}`,
                    background: active ? 'var(--color-accent)' : 'transparent',
                    color: active ? '#000' : '#fff',
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    fontFamily: 'var(--font-family-body)',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    transition: 'all 80ms ease',
                }, children: opt.label }, opt.value));
        }) }));
}
// ---------------------------------------------------------------------------
// StarRating (overlay)
// ---------------------------------------------------------------------------
function StarRating({ workId, currentRating, partyId, }) {
    const queryClient = useQueryClient();
    const handleClick = useCallback(async (star) => {
        const key = ['party', partyId, 'watchlist'];
        const clearing = star === currentRating;
        queryClient.setQueryData(key, (old) => {
            if (!old)
                return old;
            return {
                ...old,
                pages: old.pages.map((page) => ({
                    ...page,
                    items: page.items.map((item) => item.workId === workId
                        ? {
                            ...item,
                            myRating: clearing ? null : star,
                            ...(!clearing && item.myStatus !== STATUS_WATCHED
                                ? { myStatus: STATUS_WATCHED }
                                : {}),
                        }
                        : item),
                })),
            };
        });
        try {
            if (clearing) {
                await api.delete(`/users/me/works/${workId}/rating`);
            }
            else {
                await api.put(`/users/me/works/${workId}/rating`, { rating: star });
            }
        }
        catch {
            queryClient.invalidateQueries({ queryKey: key });
        }
    }, [workId, currentRating, partyId, queryClient]);
    return (_jsx("div", { style: { display: 'flex', gap: '2px' }, children: [1, 2, 3, 4, 5].map((star) => {
            const filled = currentRating != null && star <= currentRating;
            return (_jsx("button", { onClick: (e) => { e.stopPropagation(); handleClick(star); }, style: {
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '2px',
                    display: 'flex',
                    opacity: filled ? 1 : 0.4,
                    transition: 'opacity 80ms ease',
                }, children: _jsx(Star, { size: 16, fill: filled ? '#fff' : 'none', stroke: "#fff", strokeWidth: 2 }) }, star));
        }) }));
}
// ---------------------------------------------------------------------------
// WorkCard
// ---------------------------------------------------------------------------
function WorkCard({ item, partyId, isOwner, onRemoveClick }) {
    const navigate = useNavigate();
    const [overlayVisible, setOverlayVisible] = useState(false);
    function handleNavigate() {
        navigate({ to: '/works/$workId', params: { workId: item.workId }, search: { partyId } });
    }
    return (_jsxs("div", { style: { position: 'relative', cursor: 'pointer' }, onMouseEnter: () => setOverlayVisible(true), onMouseLeave: () => setOverlayVisible(false), onTouchStart: () => setOverlayVisible((v) => !v), onClick: handleNavigate, children: [_jsxs("div", { style: {
                    aspectRatio: '2/3',
                    borderRadius: 'var(--radius-md)',
                    overflow: 'hidden',
                    background: 'var(--color-surface-sunken)',
                    position: 'relative',
                }, children: [item.posterUrl ? (_jsx("img", { src: tmdbImage(item.posterUrl, 'w342'), alt: item.title, loading: "lazy", style: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' } })) : (_jsx("div", { style: { width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }, children: _jsx(Film, { size: 28, style: { color: 'var(--color-text-faint)' } }) })), _jsxs("div", { style: {
                            position: 'absolute',
                            inset: 0,
                            background: 'rgba(0,0,0,0.72)',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '10px',
                            padding: '12px',
                            opacity: overlayVisible ? 1 : 0,
                            transition: 'opacity 120ms ease',
                            pointerEvents: overlayVisible ? 'auto' : 'none',
                        }, children: [isOwner && onRemoveClick && (_jsx("button", { onClick: (e) => { e.stopPropagation(); onRemoveClick(item.workId, item.title); }, style: {
                                    position: 'absolute',
                                    top: '8px',
                                    right: '8px',
                                    background: 'rgba(0,0,0,0.6)',
                                    border: '1px solid rgba(255,255,255,0.2)',
                                    borderRadius: 'var(--radius-full)',
                                    width: '28px',
                                    height: '28px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    padding: 0,
                                }, title: "Remove from Watch List", children: _jsx(Trash2, { size: 14, color: "#fff" }) })), _jsx("div", { onClick: (e) => e.stopPropagation(), children: _jsx(WatchStatusControl, { workId: item.workId, currentStatus: item.myStatus, partyId: partyId }) }), _jsx("div", { onClick: (e) => e.stopPropagation(), children: _jsx(StarRating, { workId: item.workId, currentRating: item.myRating, partyId: partyId }) })] })] }), _jsxs("div", { style: { marginTop: '8px' }, children: [_jsx("p", { style: {
                            margin: 0,
                            fontSize: '0.8rem',
                            fontWeight: 700,
                            color: 'var(--color-text)',
                            fontFamily: 'var(--font-family-body)',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }, children: item.title }), _jsxs("p", { style: {
                            margin: 0,
                            fontSize: '0.7rem',
                            color: 'var(--color-text-muted)',
                            fontFamily: 'var(--font-family-body)',
                        }, children: [item.releaseYear ?? '', item.releaseYear ? ' \u00B7 ' : '', workTypeLabel(item.workType)] })] })] }));
}
function WatchListTab({ partyId, watchList, isLoading, maxWorks, totalCount, hasNextPage, isFetchingNextPage, onLoadMore, isOwner, }) {
    const queryClient = useQueryClient();
    const [statusFilter, setStatusFilter] = useState('all');
    const [workToRemove, setWorkToRemove] = useState(null);
    const [ties, setTies] = useState(null);
    const [isRemoving, setIsRemoving] = useState(false);
    async function handleRemoveClick(workId, title) {
        try {
            const tiesData = await api.get(`/parties/${partyId}/works/${workId}/ties`);
            const hasTies = tiesData.discussionCount > 0 || tiesData.messageCount > 0 || tiesData.activityFeedCount > 0;
            if (!hasTies) {
                await api.delete(`/parties/${partyId}/works/${workId}`);
                queryClient.invalidateQueries({ queryKey: ['party', partyId, 'watchlist'] });
                queryClient.invalidateQueries({ queryKey: ['party', partyId, 'work-ids'] });
                queryClient.invalidateQueries({ queryKey: ['party', partyId, 'activity'] });
            }
            else {
                setWorkToRemove({ workId, title });
                setTies(tiesData);
            }
        }
        catch {
            // fail silently
        }
    }
    async function handleConfirmRemove() {
        if (!workToRemove)
            return;
        setIsRemoving(true);
        try {
            await api.delete(`/parties/${partyId}/works/${workToRemove.workId}`);
            queryClient.invalidateQueries({ queryKey: ['party', partyId, 'watchlist'] });
            queryClient.invalidateQueries({ queryKey: ['party', partyId, 'work-ids'] });
            queryClient.invalidateQueries({ queryKey: ['party', partyId, 'activity'] });
            setWorkToRemove(null);
            setTies(null);
        }
        catch {
            // fail silently
        }
        finally {
            setIsRemoving(false);
        }
    }
    // Filter
    const filteredWorks = watchList.filter((item) => statusMatchesFilter(item.myStatus, statusFilter));
    return (_jsxs(_Fragment, { children: [_jsx("div", { style: {
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    marginBottom: '20px', flexWrap: 'wrap', gap: '8px',
                }, children: _jsxs("p", { style: {
                        fontFamily: 'var(--font-family-body)',
                        fontSize: '0.8rem',
                        color: 'var(--color-text-muted)',
                        margin: 0,
                    }, children: [totalCount, " / ", maxWorks, " titles"] }) }), _jsx("div", { style: { display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '20px' }, children: Object.keys(filterLabels).map((f) => (_jsx("button", { onClick: () => setStatusFilter(f), style: {
                        padding: '4px 12px',
                        borderRadius: 'var(--radius-full)',
                        border: `1px solid ${statusFilter === f ? 'var(--color-text)' : 'var(--color-border)'}`,
                        background: statusFilter === f ? 'var(--color-text)' : 'transparent',
                        color: statusFilter === f ? 'var(--color-bg)' : 'var(--color-text-muted)',
                        fontSize: '0.75rem',
                        fontWeight: 700,
                        fontFamily: 'var(--font-family-body)',
                        cursor: 'pointer',
                        transition: 'all 80ms ease',
                    }, children: filterLabels[f] }, f))) }), isLoading ? (_jsx("div", { style: { display: 'flex', justifyContent: 'center', padding: '64px 0' }, children: _jsx("div", { style: {
                        width: '24px',
                        height: '24px',
                        border: '2px solid var(--color-border)',
                        borderTopColor: 'var(--color-text)',
                        borderRadius: '50%',
                        animation: 'spin 0.6s linear infinite',
                    } }) })) : filteredWorks.length === 0 ? (_jsxs("div", { style: { textAlign: 'center', padding: '64px 0' }, children: [_jsx(Film, { size: 36, style: { color: 'var(--color-text-faint)', marginBottom: '12px', opacity: 0.4 } }), _jsx("p", { style: { fontFamily: 'var(--font-family-body)', fontSize: '0.875rem', color: 'var(--color-text-faint)' }, children: statusFilter === 'all'
                            ? 'Nothing on the watch list yet. Head to Discover to add something.'
                            : 'No works match this filter.' })] })) : (_jsxs(_Fragment, { children: [_jsx("div", { className: "watchlist-grid", children: filteredWorks.map((item) => (_jsx(WorkCard, { item: item, partyId: partyId, isOwner: isOwner, onRemoveClick: handleRemoveClick }, item.workId))) }), hasNextPage && statusFilter === 'all' && (_jsx("div", { style: { display: 'flex', justifyContent: 'center', padding: '24px 0' }, children: _jsx("button", { onClick: onLoadMore, disabled: isFetchingNextPage, style: {
                                padding: '10px 24px',
                                borderRadius: 'var(--radius-md)',
                                border: '1px solid var(--color-border)',
                                background: isFetchingNextPage ? 'var(--color-surface-sunken)' : 'var(--color-surface)',
                                color: isFetchingNextPage ? 'var(--color-text-faint)' : 'var(--color-text)',
                                fontSize: '0.875rem',
                                fontWeight: 700,
                                fontFamily: 'var(--font-family-body)',
                                cursor: isFetchingNextPage ? 'not-allowed' : 'pointer',
                                transition: 'all 80ms ease',
                            }, children: isFetchingNextPage ? 'Loading...' : 'Load More' }) }))] })), _jsxs(Dialog, { open: !!workToRemove, onOpenChange: (open) => { if (!open) {
                    setWorkToRemove(null);
                    setTies(null);
                } }, children: [_jsxs(DialogHeader, { children: [_jsxs(DialogTitle, { children: ["Remove \"", workToRemove?.title, "\"?"] }), _jsx(DialogDescription, { children: "This will permanently delete:" })] }), ties && (_jsxs("ul", { style: { margin: '8px 0 0', paddingLeft: '20px', fontSize: '0.875rem', color: 'var(--color-text-muted)', fontFamily: 'var(--font-family-body)', lineHeight: 1.8 }, children: [ties.discussionCount > 0 && _jsxs("li", { children: [ties.discussionCount, " discussion", ties.discussionCount !== 1 ? 's' : ''] }), ties.messageCount > 0 && _jsxs("li", { children: [ties.messageCount, " message", ties.messageCount !== 1 ? 's' : ''] }), ties.activityFeedCount > 0 && _jsxs("li", { children: [ties.activityFeedCount, " activity feed entr", ties.activityFeedCount !== 1 ? 'ies' : 'y'] })] })), _jsxs(DialogFooter, { children: [_jsx("button", { onClick: () => { setWorkToRemove(null); setTies(null); }, style: {
                                    padding: '8px 16px', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)',
                                    background: 'transparent', color: 'var(--color-text)', fontSize: '0.875rem', fontWeight: 600,
                                    fontFamily: 'var(--font-family-body)', cursor: 'pointer',
                                }, children: "Cancel" }), _jsx("button", { onClick: handleConfirmRemove, disabled: isRemoving, style: {
                                    padding: '8px 16px', borderRadius: 'var(--radius-md)', border: 'none',
                                    background: 'var(--color-danger, #ef4444)', color: '#fff', fontSize: '0.875rem', fontWeight: 600,
                                    fontFamily: 'var(--font-family-body)', cursor: isRemoving ? 'not-allowed' : 'pointer',
                                    opacity: isRemoving ? 0.6 : 1,
                                }, children: isRemoving ? 'Removing...' : 'Remove' })] })] })] }));
}
// ---------------------------------------------------------------------------
// PageSpinner
// ---------------------------------------------------------------------------
function PageSpinner() {
    return (_jsxs("div", { style: { display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }, children: [_jsx("style", { children: `@keyframes spin { to { transform: rotate(360deg) } }` }), _jsx("div", { style: {
                    width: '28px',
                    height: '28px',
                    border: '2px solid var(--color-border)',
                    borderTopColor: 'var(--color-text)',
                    borderRadius: '50%',
                    animation: 'spin 0.6s linear infinite',
                } })] }));
}
// ---------------------------------------------------------------------------
// SettingsTab
// ---------------------------------------------------------------------------
function SettingsTab({ partyId, party }) {
    const queryClient = useQueryClient();
    const [name, setName] = useState(party.name);
    const [isSavingName, setIsSavingName] = useState(false);
    const [nameSuccess, setNameSuccess] = useState(false);
    const [nameError, setNameError] = useState(null);
    const [allowMemberAddContent, setAllowMemberAddContent] = useState(party.allowMemberAddContent);
    const [isTogglingAccess, setIsTogglingAccess] = useState(false);
    async function handleSaveName() {
        const trimmed = name.trim();
        if (!trimmed) {
            setNameError('Party name is required.');
            return;
        }
        setNameError(null);
        setNameSuccess(false);
        setIsSavingName(true);
        try {
            await api.put(`/parties/${partyId}`, { name: trimmed });
            queryClient.invalidateQueries({ queryKey: ['party', partyId] });
            setNameSuccess(true);
            setTimeout(() => setNameSuccess(false), 2000);
        }
        catch {
            setNameError('Failed to update name.');
        }
        finally {
            setIsSavingName(false);
        }
    }
    async function handleToggleAccess() {
        const newValue = !allowMemberAddContent;
        setIsTogglingAccess(true);
        try {
            await api.put(`/parties/${partyId}`, { allowMemberAddContent: newValue });
            setAllowMemberAddContent(newValue);
            queryClient.invalidateQueries({ queryKey: ['party', partyId] });
        }
        catch {
            // revert on failure
        }
        finally {
            setIsTogglingAccess(false);
        }
    }
    return (_jsxs("div", { style: { maxWidth: '480px' }, children: [_jsxs("div", { style: { marginBottom: '32px' }, children: [_jsx("label", { style: {
                            display: 'block',
                            fontFamily: 'var(--font-family-body)',
                            fontSize: '0.75rem', fontWeight: 700,
                            letterSpacing: '0.08em', textTransform: 'uppercase',
                            color: 'var(--color-text-muted)',
                            marginBottom: '8px',
                        }, children: "Party Name" }), _jsxs("div", { style: { display: 'flex', gap: '8px' }, children: [_jsx("input", { value: name, onChange: e => setName(e.target.value), maxLength: 100, style: {
                                    flex: 1,
                                    padding: '10px 12px',
                                    borderRadius: 'var(--radius-md)',
                                    border: '1px solid var(--color-border)',
                                    background: 'var(--color-surface)',
                                    color: 'var(--color-text)',
                                    fontFamily: 'var(--font-family-body)',
                                    fontSize: '0.875rem',
                                    outline: 'none',
                                    transition: 'border-color 80ms ease',
                                }, onFocus: e => (e.target.style.borderColor = 'var(--color-border-strong)'), onBlur: e => (e.target.style.borderColor = 'var(--color-border)') }), _jsx("button", { onClick: handleSaveName, disabled: isSavingName || name.trim() === party.name, style: {
                                    padding: '10px 20px',
                                    borderRadius: 'var(--radius-md)',
                                    border: 'none',
                                    background: isSavingName || name.trim() === party.name ? 'var(--color-surface-sunken)' : 'var(--color-accent)',
                                    color: isSavingName || name.trim() === party.name ? 'var(--color-text-faint)' : 'var(--color-text-inverse)',
                                    fontFamily: 'var(--font-family-body)',
                                    fontSize: '0.875rem', fontWeight: 700,
                                    cursor: isSavingName || name.trim() === party.name ? 'not-allowed' : 'pointer',
                                    transition: 'color 200ms, background-color 200ms, box-shadow 200ms',
                                    whiteSpace: 'nowrap',
                                }, children: isSavingName ? 'Saving...' : 'Save' })] }), nameError && (_jsx("p", { style: { marginTop: '6px', fontSize: '0.8rem', color: 'var(--color-danger)', fontFamily: 'var(--font-family-body)' }, children: nameError })), nameSuccess && (_jsx("p", { style: { marginTop: '6px', fontSize: '0.8rem', color: 'var(--color-success)', fontFamily: 'var(--font-family-body)' }, children: "Name updated." }))] }), _jsxs("div", { children: [_jsx("label", { style: {
                            display: 'block',
                            fontFamily: 'var(--font-family-body)',
                            fontSize: '0.75rem', fontWeight: 700,
                            letterSpacing: '0.08em', textTransform: 'uppercase',
                            color: 'var(--color-text-muted)',
                            marginBottom: '8px',
                        }, children: "Permissions" }), _jsxs("div", { style: {
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            padding: '16px',
                            borderRadius: 'var(--radius-md)',
                            border: '1px solid var(--color-border)',
                            background: 'var(--color-surface)',
                        }, children: [_jsxs("div", { children: [_jsx("p", { style: {
                                            margin: 0,
                                            fontFamily: 'var(--font-family-body)',
                                            fontSize: '0.875rem', fontWeight: 600,
                                            color: 'var(--color-text)',
                                        }, children: "Members can add works" }), _jsx("p", { style: {
                                            margin: '4px 0 0',
                                            fontFamily: 'var(--font-family-body)',
                                            fontSize: '0.8rem',
                                            color: 'var(--color-text-muted)',
                                        }, children: "Allow all members to add movies and shows to the watch list." })] }), _jsx("button", { onClick: handleToggleAccess, disabled: isTogglingAccess, role: "switch", "aria-checked": allowMemberAddContent, style: {
                                    flexShrink: 0,
                                    width: '44px', height: '24px',
                                    borderRadius: 'var(--radius-full)',
                                    border: 'none',
                                    background: allowMemberAddContent ? 'var(--color-accent)' : 'var(--color-border-strong)',
                                    cursor: isTogglingAccess ? 'not-allowed' : 'pointer',
                                    position: 'relative',
                                    transition: 'background 150ms ease',
                                    opacity: isTogglingAccess ? 0.6 : 1,
                                    marginLeft: '16px',
                                }, children: _jsx("div", { style: {
                                        width: '18px', height: '18px',
                                        borderRadius: '50%',
                                        background: '#ffffff',
                                        position: 'absolute',
                                        top: '3px',
                                        left: allowMemberAddContent ? '23px' : '3px',
                                        transition: 'left 150ms ease',
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                                    } }) })] })] })] }));
}
// ---------------------------------------------------------------------------
// PartyDetailPage
// ---------------------------------------------------------------------------
export function PartyDetailPage() {
    const { partyId } = useParams({ strict: false });
    const auth = useAuth();
    const { data: party, isLoading: partyLoading } = useQuery({
        queryKey: ['party', partyId],
        queryFn: () => api.get(`/parties/${partyId}`),
    });
    const { data: watchListData, isLoading: watchListLoading, hasNextPage, isFetchingNextPage, fetchNextPage, } = useInfiniteQuery({
        queryKey: ['party', partyId, 'watchlist'],
        queryFn: ({ pageParam }) => {
            const params = new URLSearchParams();
            if (pageParam)
                params.set('cursor', pageParam);
            const qs = params.toString();
            return api.get(`/parties/${partyId}/works${qs ? `?${qs}` : ''}`);
        },
        initialPageParam: null,
        getNextPageParam: (lastPage) => lastPage.nextCursor,
        enabled: !!party,
    });
    const { data: workIdsData } = useQuery({
        queryKey: ['party', partyId, 'work-ids'],
        queryFn: () => api.get(`/parties/${partyId}/work-ids`),
        enabled: !!party,
    });
    const partyWorkIds = useMemo(() => new Set(workIdsData?.workIds ?? []), [workIdsData]);
    const watchList = watchListData?.pages.flatMap((p) => p.items) ?? [];
    const totalCount = watchListData?.pages[0]?.totalCount ?? 0;
    const isOwner = party?.members?.some((m) => m.isOwner && m.userId === auth.user?.userId) ?? false;
    const search = useSearch({ strict: false });
    const validTabs = ['watchlist', 'discover', 'activity', 'members', 'discussion', 'settings'];
    const activeTab = validTabs.includes(search.tab) ? search.tab : 'watchlist';
    const navigate = useNavigate();
    function setActiveTab(tab) {
        navigate({
            to: '/parties/$partyId',
            params: { partyId },
            search: tab === 'watchlist' ? {} : { tab },
            replace: true,
        });
    }
    if (partyLoading)
        return _jsx(PageSpinner, {});
    if (!party)
        return null;
    return (_jsxs("div", { style: { maxWidth: '1200px', margin: '0 auto', padding: '24px 16px' }, children: [_jsx(PartyHeader, { party: party }), _jsx(TabNav, { activeTab: activeTab, onChange: setActiveTab, isOwner: isOwner }), _jsxs("div", { children: [activeTab === 'watchlist' && (_jsx(WatchListTab, { partyId: partyId, watchList: watchList, isLoading: watchListLoading, maxWorks: party.maxWorks, totalCount: totalCount, hasNextPage: hasNextPage ?? false, isFetchingNextPage: isFetchingNextPage, onLoadMore: () => fetchNextPage(), isOwner: isOwner })), activeTab === 'discover' && (_jsx(DiscoverTab, { partyId: partyId, canAddContent: isOwner || party.allowMemberAddContent, partyWorkIds: partyWorkIds })), activeTab === 'activity' && (_jsx(ActivityFeedTab, { partyId: partyId })), activeTab === 'members' && (_jsx(MembersTab, { partyId: partyId, party: party, isOwner: isOwner })), activeTab === 'discussion' && (_jsx(DiscussionTab, { partyId: partyId })), activeTab === 'settings' && isOwner && (_jsx(SettingsTab, { partyId: partyId, party: party }))] })] }));
}
